import React from 'react';
import { useLocation, useOutlet } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { Analytics as VercelWebAnalytics } from '@vercel/analytics/react';
import ClientProviders from '@/components/ClientProviders/ClientProviders';
import LayoutWrapper from '@/components/LayoutWrapper/LayoutWrapper';
import NativeDeepLinkListener from '@/components/NativeDeepLinkListener/NativeDeepLinkListener';
import OfflineBanner from '@/components/OfflineBanner/OfflineBanner';
import TripImportRouteModalHost from '@/components/TripImport/TripImportRouteModalHost';
import InspoImportRouteModalHost from '@/components/InspoImport/InspoImportRouteModalHost';
import AccountSettingsHost from '@/components/AccountSettings/AccountSettingsHost';
import PageTransition from '@/components/PageTransition/PageTransition';
import { useNativeStatusBar } from '@/hooks/useNativeStatusBar';
import { useKeyboardInset } from '@/hooks/useKeyboardInset';
import { useNativeAppLifecycle } from '@/hooks/useNativeAppLifecycle';
import { useNativeBackButton } from '@/hooks/useNativeBackButton';
import { useNativeSplashScreen } from '@/hooks/useNativeSplashScreen';
import { useSmartAppBanner } from '@/hooks/useSmartAppBanner';
import { useNativePushNotifications } from '@/hooks/useNativePushNotifications';
import { useNotificationPolling } from '@/hooks/useNotificationPolling';
import { isNativePlatform } from '@/lib/platform';

// Query params a route consumes once on mount and then strips back out of the URL
// (ListRouteClient / ItineraryRouteClient `?addMode`). They must not take part in the
// transition key: stripping one would otherwise re-key this component and remount the page
// that just consumed the param, throwing away the one-shot state it was carrying.
const TRANSIENT_TRANSITION_PARAMS = ['addMode'];

function stripTransientParams(search: string): string {
    if (!search) return search;
    const params = new URLSearchParams(search);
    let changed = false;
    for (const key of TRANSIENT_TRANSITION_PARAMS) {
        if (params.has(key)) {
            params.delete(key);
            changed = true;
        }
    }
    // Return the original string untouched when there's nothing to strip, so every existing
    // transition key stays byte-identical.
    if (!changed) return search;
    const rest = params.toString();
    return rest ? `?${rest}` : '';
}

// Tab routes that swap content within the same page shouldn't retrigger the
// framer-motion slide. Collapse them to a stable transition key here.
export function pageTransitionKey(pathname: string, search: string): string {
    const normalizedSearch = stripTransientParams(search);
    if (/^\/my-travel\/(lists|trips)\/?$/.test(pathname)) {
        return `/my-travel${normalizedSearch}`;
    }
    return `${pathname}${normalizedSearch}`;
}

function RootLayoutNativeHooks() {
    useNativeStatusBar();
    useNativePushNotifications();
    useNotificationPolling();
    useKeyboardInset();
    useNativeAppLifecycle();
    useNativeBackButton();
    useNativeSplashScreen();
    useSmartAppBanner();
    return null;
}

export default function RootLayout() {
    const location = useLocation();
  const outlet = useOutlet();

    return (
        <ClientProviders>
            <RootLayoutNativeHooks />
            <NativeDeepLinkListener />
            <OfflineBanner />
            <LayoutWrapper>
                <AnimatePresence mode={isNativePlatform() ? 'sync' : 'wait'} initial={false}>
          <PageTransition key={pageTransitionKey(location.pathname, location.search)}>
            {outlet}
                    </PageTransition>
                </AnimatePresence>
            </LayoutWrapper>
      <InspoImportRouteModalHost />
      <TripImportRouteModalHost />
      <AccountSettingsHost />
            {!isNativePlatform() && <VercelWebAnalytics />}
        </ClientProviders>
    );
}
